import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BookSession } from "@/components/book-session";
import RecentColleges from "@/components/recentpost";
import Head from "next/head";
import ErrorPage from "next/error";
import CounsellingModal from "../CounsellingModal";
import Link from "next/link";
import Container from "@/components/container";
import Header from "@/components/header";
import Layout from "@/components/layout";
import PostHeader from "@/components/post-header";
import PostBody from "@/components/post-body";
import ReviewSection from "@/components/ReviewSection";
import { useFAQToggleHandler, FAQ_TOGGLE_STYLES } from "@/components/FAQToggleHandler";
import Cookies from "js-cookie";

export const getServerSideProps = async (context) => {
  const { slug } = context.params;
  const baseURL = process.env.NEXT_PUBLIC_GHOST_API_URL;
  const apiKey = process.env.NEXT_PUBLIC_GHOST_API_KEY;

  try {
    const res = await axios.get(
      `${baseURL}/ghost/api/v3/content/posts/slug/${slug}/?key=${apiKey}&include=authors`
    );
    const selectedCounselling = res.data.posts[0];
    
    // Ensure primary_author is set from authors array
    if (selectedCounselling && !selectedCounselling.primary_author && selectedCounselling.authors?.length > 0) {
      selectedCounselling.primary_author = selectedCounselling.authors[0];
    }

    if (selectedCounselling) {
      selectedCounselling.html = selectedCounselling.html.replace(
        /ghost\.neetsupport\.com\/content\//g,
        "neetsupport.com/content/"
      );
      selectedCounselling.html = selectedCounselling.html.replace(
        /ghost\.neetsupport\.com/g,
        selectedCounselling.canonical_url
      );

      // Update feature_image URL
      if (selectedCounselling.feature_image) {
        selectedCounselling.feature_image =
          selectedCounselling.feature_image.replace(
            /ghost\.neetsupport\.com/g,
            "neetsupport.com"
          );
      }

      // Ensure every link opens in a new tab and remove query parameters
      const { JSDOM } = await import("jsdom");
      const dom = new JSDOM(selectedCounselling.html);
      const { document } = dom.window;
      document.querySelectorAll("a").forEach((link) => {
        if (link.hasAttribute("href")) {
          const href = link.getAttribute("href");
          const cleanHref = href.split("?")[0];
          link.setAttribute("href", cleanHref);
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        }
      });
      selectedCounselling.html = document.body.innerHTML;

      return {
        props: {
          fetchedCounselling: (({ url, ...rest }) => rest)(selectedCounselling),
        },
      };
    } else {
      return { notFound: true };
    }
  } catch (error) {
    console.error("Error fetching counselling data:", error);
    return { notFound: true };
  }
};

const PrevArrow = ({ className, onClick }) => (
  <button
    className={`absolute left-0 z-10 transform -translate-y-1/2 top-1/2 ${className}`}
    onClick={onClick}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6 text-gray-700 hover:text-orange-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  </button>
);

const NextArrow = ({ className, onClick }) => (
  <button
    className={`absolute right-0 z-10 transform -translate-y-1/2 top-1/2 ${className}`}
    onClick={onClick}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6 text-gray-700 hover:text-orange-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  </button>
);

const CounsellingDetails = ({ fetchedCounselling }) => {
  const router = useRouter();
  const { slug } = router.query;

  const [modalOpen, setModalOpen] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const scrollTimeoutRef = useRef(null);
  
  // Review section state
  const [userRating, setUserRating] = useState(0);
  const [userReviews, setUserReviews] = useState([]);
  const [reviewContent, setReviewContent] = useState({ content: "" });
  const [hasVoted, setHasVoted] = useState({});
  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  const isLoggedIn = !!Cookies.get("authToken");
  const userId = isLoggedIn ? Cookies.get("userId") : null;
  const userName = isLoggedIn ? Cookies.get("userName") : null;

  // Initialize FAQ toggle handler
  useFAQToggleHandler([fetchedCounselling?.id]);

  useEffect(() => {
    if (fetchedCounselling) {
      // URL replacements are already done on server-side
      // No need to process HTML again on client-side
    }
  }, [fetchedCounselling]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setModalOpen(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [slug]);

  // Handle scroll to trigger modal every 30 seconds while scrolling
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set new timeout for 30 seconds
      scrollTimeoutRef.current = setTimeout(() => {
        if (!isFormSubmitted) {
          setModalOpen(true);
        }
      }, 30000);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isFormSubmitted]);

  if (!fetchedCounselling) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <Layout>
      <Container>
        <Header />
        <article>
          <Head>
            <title>
              {fetchedCounselling ? `${fetchedCounselling.title}` : "Loading..."}
            </title>
            <link rel="shortcut icon" href="/favicon.ico" />
            <meta
              name="description"
              content={
                fetchedCounselling
                  ? fetchedCounselling.meta_description
                  : "Counselling session details are loading."
              }
            />
            <meta
              property="og:title"
              content={
                fetchedCounselling ? fetchedCounselling.og_title : "Loading..."
              }
            />
            <meta
              property="og:description"
              content={
                fetchedCounselling
                  ? fetchedCounselling.og_description
                  : "Counselling session details are loading."
              }
            />
            <meta
              property="og:image"
              content={
                fetchedCounselling
                  ? fetchedCounselling.og_image
                  : "/default-og-image.jpg"
              }
            />
            <meta
              property="og:url"
              content={
                fetchedCounselling
                  ? `${fetchedCounselling.url}`
                  : "https://neetsupport.com"
              }
            />
            <meta name="twitter:card" content="summary_large_image" />
            <link
              rel="canonical"
              href={
                fetchedCounselling
                  ? `${fetchedCounselling.canonical_url}`
                  : "https://neetsupport.com"
              }
            />
            <style>{FAQ_TOGGLE_STYLES}</style>
            {fetchedCounselling?.codeinjection_head && (
              <div
                dangerouslySetInnerHTML={{
                  __html: fetchedCounselling.codeinjection_head,
                }}
              />
            )}
          </Head>
          
          <PostHeader
            title={fetchedCounselling.title}
            coverImage={fetchedCounselling.feature_image || "/default-image.jpg"}
            date={fetchedCounselling.published_at}
            author={fetchedCounselling.primary_author}
            url={fetchedCounselling.canonical_url}
          />
          
          <PostBody content={fetchedCounselling.html} />
          
          <ReviewSection
            userReviews={userReviews}
            setUserReviews={setUserReviews}
            userRating={userRating}
            setUserRating={setUserRating}
            reviewContent={reviewContent}
            setReviewContent={setReviewContent}
            hasVoted={hasVoted}
            setHasVoted={setHasVoted}
            API_URL={API_URL}
            postId={fetchedCounselling.id}
            title={fetchedCounselling.title}
            isLoggedIn={isLoggedIn}
            userId={userId}
            userName={userName}
          />
          
          {/* Recent Counselling Posts Section */}
          {(() => {
            let isPGCounselling = false;

            try {
              const parsedExcerpt = JSON.parse(
                fetchedCounselling.custom_excerpt || "{}"
              );
              const location = parsedExcerpt.location?.toLowerCase();
              isPGCounselling =
                location?.includes("pg") ||
                fetchedCounselling.title?.toLowerCase().includes("pg");
            } catch (e) {
              console.error("Failed to parse excerpt for PG detection:", e);
            }

            const recentTag = isPGCounselling ? "pg-counselling" : "counselling";

            return (
              <div onClick={() => {
                setTimeout(() => {
                  if (!isFormSubmitted) {
                    setModalOpen(true);
                  }
                }, 3000);
              }} className="mt-12 px-4 sm:px-8 lg:px-28">
                <RecentColleges
                  tag={recentTag}
                  onCardClick={(slug) => router.push(`/counselling/${slug}`)}
                  gridCols={4}
                  showTitle={false}
                />
              </div>
            );
          })()}
        </article>
      </Container>

      {/* Counselling Modal - Triggers on scroll and initial page load */}
      <CounsellingModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          // Reset scroll timeout after closing
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }
        }}
        counselling={fetchedCounselling}
        location={
          fetchedCounselling?.custom_excerpt
            ? (() => {
                try {
                  const parsedExcerpt = JSON.parse(
                    fetchedCounselling.custom_excerpt
                  );
                  return parsedExcerpt.location || "Location Not Available";
                } catch (error) {
                  console.error("Error parsing custom_excerpt:", error);
                  return "Location Not Available";
                }
              })()
            : "Location Not Available"
        }
        onSubmit={() => {
          localStorage.setItem("counselling_form_submitted", "true");
          setIsFormSubmitted(true);
          setModalOpen(false);
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }
        }}
      />
    </Layout>
  );
};

export default CounsellingDetails;
